import { useState, useEffect } from "react"
import { useNavigate, useLocation } from "react-router-dom"
import { Button } from "@/components/ui/button"
import { Card } from "@/components/ui/card"
import { Steps } from "@/components/ui/steps"
import { SelectOpportunity } from "@/components/npo/catalog/SelectOpportunity"
import { CatalogDetails } from "@/components/npo/catalog/CatalogDetails"
import { ReviewAndPublish } from "@/components/npo/catalog/ReviewAndPublish"
import { ArrowLeft } from "lucide-react"
import { Database } from "@/integrations/supabase/types"
import { DEFAULT_CANCELLATION_POLICY, DEFAULT_CANCELLATION_TERMS } from "@/constants/forms"

type Opportunity = Database["public"]["Tables"]["opportunities"]["Row"]

export default function CreateCatalogEntry({ session, profile }: { session: any; profile: any }) {
  const navigate = useNavigate()
  const location = useLocation()
  const [currentStep, setCurrentStep] = useState(1)
  const [selectedOpportunity, setSelectedOpportunity] = useState<Opportunity | null>(null)
  const [catalogData, setCatalogData] = useState<any>({})
  const [useAI, setUseAI] = useState(false)

  useEffect(() => {
    const preselectedOpportunity = location.state?.preselectedOpportunity
    if (preselectedOpportunity) {
      setSelectedOpportunity(preselectedOpportunity)
      setCurrentStep(2) // Skip to catalog details
    }
  }, [location.state])

  const steps = [
    { number: 1, title: "Select Opportunity" },
    { number: 2, title: "Catalog Details" },
    { number: 3, title: "Review & Publish" },
  ]

  const handleNext = (option?: string) => {
    window.scrollTo(0, 0);
    
    // If AI assisted option was selected
    if (option === 'ai') {
      setUseAI(true);
    } else {
      setUseAI(false);
    }
    
    setCurrentStep((prev) => Math.min(prev + 1, 3))
  }

  const handleBack = () => {
    window.scrollTo(0, 0);
    if (currentStep === 1) {
      navigate("/dashboard/catalog")
      return
    }
    setCurrentStep((prev) => Math.max(prev - 1, 1))
  }

  return (
    <div className="space-y-8">
      <div className="flex items-center gap-4">
        <Button variant="ghost" onClick={handleBack}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back
        </Button>
        <h2 className="text-4xl font-bold tracking-tight">Create Opportunity</h2>
      </div>

      <Steps steps={steps} currentStep={currentStep} />

      <Card className="p-6">
        {currentStep === 1 && (
          <SelectOpportunity
            selectedOpportunity={selectedOpportunity}
            onSelect={setSelectedOpportunity}
            onNext={(option) => handleNext(option)}
            session={session}
            profile={profile}
          />
        )}
        {currentStep === 2 && (
          <CatalogDetails
            opportunity={selectedOpportunity}
            catalogData={catalogData}
            onDataChange={(data) => {
              setCatalogData({
                ...data,
                opportunity_id: selectedOpportunity?.id,
              })
            }}
            onBack={handleBack}
            onNext={handleNext}
            session={session}
            profile={profile}
            triggerAIGenerate={useAI}
          />
        )}
        {currentStep === 3 && (
          <ReviewAndPublish
            opportunity={selectedOpportunity}
            catalogData={catalogData}
            onBack={handleBack}
            onComplete={() => navigate("/dashboard/catalog")}
            session={session}
            profile={profile}
          />
        )}
      </Card>
    </div>
  )
}